import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from 'sonner';
import '@/App.css';

import LandingPage from './pages/LandingPage';
import AuthPage from './pages/AuthPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import VerifyEmail from './pages/VerifyEmail';
import Dashboard from './pages/Dashboard';
import SimulationRoom from './pages/SimulationRoom';
import AdvancedSimulation from './pages/AdvancedSimulation';
import Analytics from './pages/Analytics';
import SubscriptionPage from './pages/SubscriptionPage';
import SubscriptionSuccess from './pages/SubscriptionSuccess';
import NCLEXPractice from './pages/NCLEXPractice';
import EducatorDashboard from './pages/EducatorDashboard';
import PaymentRequiredModal from './components/PaymentRequiredModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

axios.defaults.baseURL = API;

// Add response interceptor to handle auth errors globally
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid - clear auth state
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
      // Only redirect if not already on auth page
      if (!window.location.pathname.includes('/auth')) {
        window.location.href = '/auth';
      }
    }
    return Promise.reject(error);
  }
);

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUser = async () => {
    try {
      const response = await axios.get('/auth/me');
      setUser(response.data);
    } catch (error) {
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
    } finally {
      setLoading(false);
    }
  };

  const login = (token, userData) => {
    localStorage.setItem('token', token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <Toaster position="top-right" richColors />
      {/* Payment Required Modal - shows when trial expired without payment */}
      {user?.payment_required && <PaymentRequiredModal user={user} />}
      <Routes>
        <Route path="/" element={<LandingPage user={user} />} />
        <Route 
          path="/auth" 
          element={user ? <Navigate to="/dashboard" /> : <AuthPage onLogin={login} />} 
        />
        <Route 
          path="/forgot-password" 
          element={<ForgotPasswordPage />} 
        />
        <Route 
          path="/reset-password" 
          element={<ResetPasswordPage />} 
        />
        <Route 
          path="/verify-email" 
          element={<VerifyEmail />} 
        />
        <Route 
          path="/dashboard" 
          element={user ? <Dashboard user={user} onLogout={logout} /> : <Navigate to="/auth" />} 
        />
        <Route 
          path="/simulation/:sessionId" 
          element={user ? <SimulationRoom user={user} onLogout={logout} /> : <Navigate to="/auth" />} 
        />
        <Route 
          path="/advanced-simulation" 
          element={user ? <AdvancedSimulation user={user} onLogout={logout} /> : <Navigate to="/auth" />} 
        />
        <Route 
          path="/analytics" 
          element={user ? <Analytics user={user} onLogout={logout} /> : <Navigate to="/auth" />} 
        />
        <Route 
          path="/subscription" 
          element={user ? <SubscriptionPage user={user} onLogout={logout} /> : <Navigate to="/auth" />} 
        />
        <Route 
          path="/subscription-success" 
          element={user ? <SubscriptionSuccess user={user} onLogout={logout} /> : <Navigate to="/auth" />} 
        />
        <Route 
          path="/nclex-practice" 
          element={user ? <NCLEXPractice user={user} onLogout={logout} /> : <Navigate to="/auth" />} 
        />
        <Route 
          path="/educator" 
          element={user ? <EducatorDashboard user={user} onLogout={logout} /> : <Navigate to="/auth" />} 
        />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
